import { type FC, useEffect, useState } from 'react';

import { useRideRecorder } from '@/hooks/useRideRecorder';

// Start/stop ride recording. Idle: a red record dot (amber-ringed when auto-record is
// armed and waiting for movement). Recording: a pill with a pulsing dot, the elapsed
// clock, and running distance — tap to stop. Recording is local-first; upload happens on
// stop and periodically mid-ride (retries later if offline / signed out). See useRideRecorder.
const RecordButton: FC = () => {
  const { recording, armed, startedAtMs, distanceMeters, start, stop } = useRideRecorder();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [recording]);

  if (!recording) {
    const title = armed ? 'Auto-record armed — starts when you ride off (tap to start now)' : 'Record ride';
    return (
      <button className="mg-icon-btn" onClick={start} title={title} aria-label={title}>
        <span className={armed ? 'mg-rec-dot armed' : 'mg-rec-dot'} />
      </button>
    );
  }

  const elapsed = startedAtMs ? Math.max(0, Math.floor((now - startedAtMs) / 1000)) : 0;
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  // Seconds matter early ("is it recording?"); past the hour they're noise that widens
  // the pill, so the clock reformats from mm:ss to h:mm.
  const time = h > 0 ? `${h}:${mm}` : `${mm}:${ss}`;

  return (
    <button className="mg-rec-pill" onClick={() => void stop()} title="Stop recording" aria-label="Stop recording">
      <span className="mg-rec-dot recording" />
      <span className="mg-rec-time">{time}</span>
      <span className="mg-rec-dist">{(distanceMeters / 1000).toFixed(1)} km</span>
    </button>
  );
};

export default RecordButton;
